import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const TextTypeSelector = ({
    textTypesList = [],
    selectedTextTypeId = null,
    onSelectTextType
}) => {
    const { t } = useTranslation('common');
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    // Normalize text types list
    const normalizedList = useMemo(() => {
        const raw = Array.isArray(textTypesList) ? textTypesList : [];
        return raw.map((item) => ({
            TextTypeId: item.TextTypeId || item.CategoryId || item.Id,
            TextTypeName: item.TextTypeName || item.CategoryName || item.Name
        })).filter((item) => item.TextTypeId && item.TextTypeName);
    }, [textTypesList]);

    // Filter text types based on search
    const filteredList = useMemo(() => {
        if (!searchTerm.trim()) return normalizedList;
        const term = searchTerm.trim().toLowerCase();
        return normalizedList.filter((item) =>
            item.TextTypeName.toLowerCase().includes(term)
        );
    }, [normalizedList, searchTerm]);

    // Find currently selected object
    const selectedObj = useMemo(() => {
        return normalizedList.find((item) => item.TextTypeId === selectedTextTypeId);
    }, [normalizedList, selectedTextTypeId]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (id) => {
        const nextId = selectedTextTypeId === id ? null : id;
        onSelectTextType(nextId);
        setShowDropdown(false);
    };

    return (
        <div className="modern-form-section h-100 mb-0">
            <div className="modern-section-title text-info font-weight-bold d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                    <i className="icon fal fa-file-alt ml-2 text-info"></i>
                    <span>{t('QuestionView.textTypeSection') || 'نوع النص'}</span>
                </div>
                <span className="badge badge-pill badge-light text-muted small font-weight-normal border px-2 py-1">
                    {t('QuestionView.singleTextTypeNote') || '(نوع واحد فقط)'}
                </span>
            </div>

            <div className="form-group w-100 mb-0">
                <div className="modern-tree-select-wrapper" ref={dropdownRef}>
                    <div
                        className="modern-tree-select-trigger"
                        onClick={() => setShowDropdown((prev) => !prev)}
                    >
                        <div className="modern-tree-select-values">
                            {selectedObj ? (
                                <span className="font-weight-bold text-dark">{selectedObj.TextTypeName}</span>
                            ) : (
                                <span className="text-muted">
                                    {t('QuestionView.selectTextType') || 'اختر نوع النص...'}
                                </span>
                            )}
                        </div>
                        <i className={`fad fa-chevron-down arrow-icon ${showDropdown ? 'open' : ''}`}></i>
                    </div>

                    {showDropdown && (
                        <div className="modern-tree-select-dropdown">
                            <div className="modern-tree-search-wrapper">
                                <input
                                    type="text"
                                    className="form-control modern-search-input"
                                    placeholder={t('QuestionView.searchTextTypes') || 'بحث في أنواع النصوص...'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <i className="fal fa-search search-icon"></i>
                            </div>
                            <div className="modern-tree-nodes">
                                {filteredList.map((item) => {
                                    const isSelected = selectedTextTypeId === item.TextTypeId;
                                    return (
                                        <div
                                            key={item.TextTypeId}
                                            className={`modern-tree-item d-flex align-items-center justify-content-between py-2 px-3 mb-1 rounded ${
                                                isSelected ? 'bg-info text-white font-weight-bold' : ''
                                            }`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelect(item.TextTypeId);
                                            }}
                                        >
                                            <div className="d-flex align-items-center">
                                                <i className={`far ${isSelected ? 'fa-dot-circle text-white' : 'fa-circle text-muted'} ml-2`}></i>
                                                <span>{item.TextTypeName}</span>
                                            </div>
                                            {isSelected && <i className="fas fa-check text-white"></i>}
                                        </div>
                                    );
                                })}
                                {filteredList.length === 0 && (
                                    <div className="text-center text-muted p-2 small">
                                        {t('QuestionView.noMatchingTextTypes') || 'لا توجد نتائج مطابقة'}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {selectedObj && (
                    <div className="selected-topics-tags-list mt-2">
                        <div
                            className="selected-topic-chip"
                            style={{ background: '#e0f2fe', borderColor: '#7dd3fc', color: '#0369a1' }}
                        >
                            <i className="far fa-file-alt topic-chip-icon text-info"></i>
                            <span className="topic-chip-name font-weight-bold">{selectedObj.TextTypeName}</span>
                            <button
                                type="button"
                                className="topic-chip-remove"
                                title={t('Cancel') || 'حذف'}
                                onClick={() => handleSelect(selectedObj.TextTypeId)}
                            >
                                <i className="far fa-times text-danger"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(TextTypeSelector);
